/**
 * Layout system for pixel office
 * Loads and parses JSON layout data from pixel-agents format
 */

import type { LayoutData, FloorColor, TileType } from "./types/index.js";
import defaultLayoutJson from "./assets/default-layout.json";
import { TileType as TileTypes } from "./types/index.js";

// Cache the parsed layout
let cachedLayout: LayoutData | null = null;

/**
 * Get the default office layout
 */
export function getDefaultLayout(): LayoutData {
  if (cachedLayout) return cachedLayout;

  // Parse the JSON layout
  cachedLayout = defaultLayoutJson as LayoutData;
  return cachedLayout;
}

/**
 * Convert flat tile array to 2D grid
 */
export function layoutToTileMap(layout: LayoutData): TileType[][] {
  const map: TileType[][] = [];
  for (let r = 0; r < layout.rows; r++) {
    const row: TileType[] = [];
    for (let c = 0; c < layout.cols; c++) {
      row.push(layout.tiles[r * layout.cols + c] as TileType);
    }
    map.push(row);
  }
  return map;
}

/**
 * Convert HSBC color to hex color string
 * Simplified colorization - converts hue/saturation/brightness to a solid color
 */
export function hsbcToHex(color: FloorColor): string {
  // Convert HSB to RGB
  const h = ((color.h % 360) + 360) % 360; // Normalize hue to 0-360
  const s = Math.max(0, Math.min(100, 50 + color.s)) / 100; // Base saturation + offset
  const l = Math.max(0, Math.min(100, 50 + color.b)) / 100; // Base lightness + offset

  // HSL to RGB conversion
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0,
    g = 0,
    b = 0;

  if (h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  // Apply contrast adjustment
  const contrast = 1 + color.c / 100;
  r = (r - 0.5) * contrast + 0.5;
  g = (g - 0.5) * contrast + 0.5;
  b = (b - 0.5) * contrast + 0.5;

  // Convert to 0-255 range
  const toHex = (n: number) => {
    const val = Math.round(Math.max(0, Math.min(1, n + m)) * 255);
    return val.toString(16).padStart(2, "0");
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Get color for a tile based on layout data
 */
export function getTileColor(
  tileType: number,
  tileColor: FloorColor | null | undefined,
  col: number,
  row: number,
): string | null {
  // VOID tiles are transparent
  if (tileType === TileTypes.VOID) return null;

  // WALL tiles get a dark color
  if (tileType === TileTypes.WALL) {
    if (tileColor) {
      return hsbcToHex(tileColor);
    }
    return "#3A3A5C"; // Default wall color
  }

  // Floor tiles use the tileColor if provided
  if (tileColor) {
    return hsbcToHex(tileColor);
  }

  // Default floor colors based on tile type
  const defaultColors: Record<number, string> = {
    [TileTypes.FLOOR_1]: "#8B7355", // Wood brown
    [TileTypes.FLOOR_2]: "#7B6347", // Darker wood
    [TileTypes.FLOOR_3]: "#6B5B4C", // Darkest wood
    [TileTypes.FLOOR_4]: "#9B8365", // Light wood
    [TileTypes.FLOOR_5]: "#5B4B3C", // Very dark wood
    [TileTypes.FLOOR_6]: "#AB9375", // Very light wood
    [TileTypes.FLOOR_7]: "#4B3B2C", // Almost black wood
  };

  return defaultColors[tileType] || "#7B6347";
}
